import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";

// #region Components
import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import ErrorModal from "shared/components/UIElements/ErrorModal";
import DatePicker from "shared/components/FormElements/DatePicker";

// #endregion Components

// #region Utils
import { transfertypes, transferStatus } from "shared/util/types";
import { VALIDATOR_REQUIRE } from "shared/util/validators";

import { GetStartStopTime, CheckTimeString } from "shared/util/time";
import { applyTransferUpdateToStore } from "features/transfers/shared/applyTransferUpdateToStore";
// #endregion Utils

// #region Hooks
import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import { StoreContext } from "shared/context/store-context";
// #endregion Hooks

const TransferItem = ({ transfer, onClose }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const store = useContext(StoreContext);

  const ItemType = "transfer";
  const ItemEndPointType = "transfers";
  const endPointDelete = "transfers";

  const { isLoading, error, sendRequest, clearError } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [formState, inputHandler] = useForm(
    {
      school: { value: "", isValid: true },
      name: { value: "", isValid: true },
      type: { value: "", isValid: true },
      transferdate: { value: "", isValid: true },
      transfertime: { value: "", isValid: true },
      done: { value: false, isValid: true },
      vehicleRoutes: { value: [], isValid: true },
    },
    true
  );

  useEffect(() => {}, [formState.inputs.personel]);

  const itemUpdateSubmitHandler = useCallback(
    async (event) => {
      event.preventDefault();

      if (!CheckTimeString(formState.inputs.transfertime.value)) {
        window.toast.error(t("transferAnalyzer.transferItem.invalidTimeError"));
        return;
      }

      let responseData;
      try {
        responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/${ItemEndPointType}/${transfer.id}`,
          "PATCH",
          JSON.stringify({
            name: formState.inputs.name.value,
            type: formState.inputs.type.value,
            school: formState.inputs.school.value,
            transferdate: formState.inputs.transferdate.value,
            transfertime: formState.inputs.transfertime.value,
            done: formState.inputs.done.value,
            //vehicleRoutes: currentVehicleRoutes,
          }),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          }
        );
        applyTransferUpdateToStore(store, responseData);
        window.toast.success(t("updateSuccess"));
      } catch (err) {
        window.toast.error(t("actionFailed"));
      }
    },
    [
      formState.inputs.transfertime.value,
      formState.inputs.name.value,
      formState.inputs.type.value,
      formState.inputs.school.value,
      formState.inputs.transferdate.value,
      formState.inputs.done.value,
      sendRequest,
      transfer.id,
      auth.token,
      store,
      t,
    ]
  );

  return (
    <React.Fragment>
      <ErrorModal error={error} onClear={clearError} />
      <DeleteModal
        endPoint={endPointDelete}
        itemType={ItemType}
        itemId={transfer.id}
        onDeleted={async () => {
          store.DeleteTransfer(transfer.id);

          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {transfer && (
        <div>
          <form className="item-form" onSubmit={itemUpdateSubmitHandler}>
            <div className="horizontal-flex" style={{ justifyContent: "flex-start" }}>
              <div style={{ flex: 3 }}>
                <CustomSelect
                  options={store.schoolOptions}
                  id="school"
                  onInput={inputHandler}
                  label={t("transferAnalyzer.transferItem.schoolLabel")}
                  initialValue={store.schoolOptions.find(
                    (school) => school.value.toString() === transfer.school.toString()
                  )}
                  validators={[VALIDATOR_REQUIRE()]}
                  initialValid={true}
                ></CustomSelect>
              </div>

              <div style={{ flex: 3 }}>
                <Input
                  id="name"
                  element="input"
                  type="text"
                  label={t("transferAnalyzer.transferItem.nameLabel")}
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText={t("transferAnalyzer.transferItem.nameError")}
                  onInput={inputHandler}
                  initialValue={transfer.name}
                  initialValid={true}
                />
              </div>

              <div style={{ flex: 1 }}>
                <CustomSelect
                  options={transfertypes}
                  id="type"
                  onInput={inputHandler}
                  label={t("transferAnalyzer.transferItem.typeLabel")}
                  initialValue={transfertypes.find((type) => type.value.toString() === transfer.type.toString())}
                  validators={[VALIDATOR_REQUIRE()]}
                  initialValid={true}
                ></CustomSelect>
              </div>

              <div style={{ flex: 1 }}>
                <DatePicker
                  id="transferdate"
                  onInput={inputHandler}
                  label={t("transferAnalyzer.transferItem.dateLabel")}
                  initialValue={transfer.transferdate}
                  initialValid={true}
                ></DatePicker>
              </div>

              <div style={{ flex: 1 }}>
                <Input
                  id="transfertime"
                  key={"transfertime-" + formState.inputs.type.value}
                  element="input"
                  type="text"
                  label={
                    formState.inputs.type.value === 100
                      ? t("pickUpRouteClientArrivalTime")
                      : t("dropOffRouteClientArrivalTime")
                  }
                  // label={t("transferAnalyzer.transferItem.timeLabel")}
                  validators={[VALIDATOR_REQUIRE()]}
                  errorText={t("transferAnalyzer.transferItem.timeError")}
                  onInput={inputHandler}
                  initialValue={GetStartStopTime(transfer.transfertime)}
                  initialValid={true}
                />
              </div>

              <div style={{ flex: 1 }}>
                <CustomSelect
                  options={transferStatus}
                  id="done"
                  onInput={inputHandler}
                  label={t("transferAnalyzer.transferItem.statusLabel")}
                  initialValue={transferStatus.find((status) => status.value.toString() === transfer.done.toString())}
                  validators={[VALIDATOR_REQUIRE()]}
                  initialValid={true}
                ></CustomSelect>
              </div>
            </div>

            {/* <div style={{ maxWidth: "500px" }}></div> */}

            <FormFooter
              isNewItem={false}
              onUpdate={itemUpdateSubmitHandler}
              onDelete={() => setShowConfirmModal(true)}
              isUpdating={isLoading}
              disabled={!formState.isValid || !formState.inputs.school.value || isLoading}
            />
          </form>
        </div>
      )}
    </React.Fragment>
  );
};

export default TransferItem;
